/**
 * The court sheet, and what a member is holding.
 *
 * This screen is drawn as a grid — hours down, courts across — and not as
 * the list the tee sheet is, because the two objects are different. A tee
 * sheet has one course and a hundred times on it, so time is the only
 * axis. A court sheet has eight courts and thirteen hours, and the
 * question the desk is asked forty times a day is "what have you got at
 * six", which is a question about a ROW. A list of courts each with their
 * own times makes that question take eight scrolls.
 *
 * The grid also does something a list cannot: it shows the ground. When
 * Court 3 is booked at ten, the pickleball courts painted on it are
 * unsellable at ten — and in a list that fact is eight rows away from the
 * reservation that caused it. Side by side, it is the cell next door, and
 * it says whose court took it.
 *
 * Every refusal from the server is shown as written. "TENNIS-4: Court 3
 * is the same ground as Pickleball 3A, and it is taken at 10:00" is the
 * whole answer, and a paraphrase would throw away the part the member
 * standing at the desk needs to hear.
 */

import { Fragment, useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { BylawNotices, Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';
import { clubToday, dayLabel, firstVisible, shiftDate, hhmm } from '../lib/sheet.ts';
import { AccountLookup, type AccountHit } from '../components/Lookup.tsx';
import { date as fmtDate } from '../lib/format.ts';

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

interface Court {
  court_id: string;
  code: string;
  name: string;
  sport: string;
  surface: string;
  enclosure: string;
  has_lights: boolean;
}

interface Player {
  player_id: string;
  position: number;
  player_kind: string;
  person_id: string | null;
  name: string | null;
  sponsor_name: string | null;
  checked_in_at: string | null;
  charged: boolean;
}

interface Reservation {
  reservation_id: string;
  court_slot_id: string;
  status: string;
  play_kind: string;
  players: Player[];
}

interface Slot {
  court_slot_id: string;
  court_id: string;
  court_code: string;
  court_name: string;
  sport: string;
  play_date: string;
  starts_at: string;
  slot_minutes: number;
  max_players: number;
  slot_status: string;
  slot_note: string | null;
  day_type: string;
  players_booked: number;
  spaces_left: number;
  reservation_count: number;
  taken_by_court_name: string | null;
  taken_by_member: string | null;
  reservations: Reservation[];
}

interface Sheet {
  date: string;
  day_type: string | null;
  courts: Court[];
  slots: Slot[];
}

interface MyReservation {
  reservation_id: string;
  court_name: string;
  sport: string;
  play_date: string;
  starts_at: string;
  slot_minutes: number;
  status: string;
  play_kind: string;
  canceled_late: boolean | null;
}

/* ------------------------------------------------------------------ */
/* Small shared bits                                                   */
/* ------------------------------------------------------------------ */

/** Postgres hands back 09:00:00; a court sheet says 09:00. */


/**
 * Anchored at noon UTC on purpose: adding a day to a midnight date
 * rolls backwards across a daylight saving boundary, and a sheet that
 * skips a Sunday every spring is a bug nobody reproduces on a Tuesday.
 */
const SURFACE_LABEL: Record<string, string> = {
  hard: 'hard',
  clay: 'clay',
  har_tru: 'Har-Tru',
  grass: 'grass',
  carpet: 'carpet',
  synthetic: 'synthetic',
};

/**
 * The sport, said out loud.
 *
 * A club that names its pickleball courts "Pickleball 1A" has already
 * said it and this is redundant; a club whose pickleball courts are
 * "Court 7" and "Court 8" has not, and a member turning up with the
 * wrong racquet is the whole cost. So it is said on the reservation
 * itself rather than only in the column heading — on a phone the sheet
 * scrolls sideways and the heading is off the screen by the time
 * anybody reads a booking.
 */
const SPORT_LABEL: Record<string, string> = {
  tennis: 'Tennis',
  pickleball: 'Pickleball',
};

const sportLabel = (sport: string) => SPORT_LABEL[sport] ?? sport;

function courtNote(c: Court): string {
  const bits = [sportLabel(c.sport), SURFACE_LABEL[c.surface] ?? c.surface];
  if (c.enclosure !== 'outdoor') bits.push(c.enclosure);
  if (c.has_lights) bits.push('lights');
  return bits.join(' · ');
}

/* ------------------------------------------------------------------ */
/* The sheet                                                           */
/* ------------------------------------------------------------------ */

export function CourtSheetPage() {
  const { atLeast, club } = useSession();
  const isStaff = atLeast('staff');
  const canOpenSheet = atLeast('manager');
  const zone = club?.timezone;

  const [date, setDate] = useState(() => clubToday(zone));
  // The sheet opens at the hour containing now; this puts the morning
  // back. See lib/sheet.ts for why that is the default.
  const [wholeDay, setWholeDay] = useState(false);
  useEffect(() => setWholeDay(false), [date]);
  const [sport, setSport] = useState<'all' | 'tennis' | 'pickleball'>('all');
  const [booking, setBooking] = useState<Slot | null>(null);
  const [open, setOpen] = useState<{ reservation: Reservation; slot: Slot } | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const sheet = useApi<Sheet>(
    `/v1/tennis/court-sheet?date=${date}${sport === 'all' ? '' : `&sport=${sport}`}`,
    [date, sport],
  );

  const openWeek = useMutation(async () => {
    const result = await api<{ total_slots: number }>('/v1/tennis/court-sheet/open', {
      method: 'POST',
      body: { from_date: date, through_date: shiftDate(date, 6) },
    });
    setNote(
      result.total_slots === 0
        ? 'The sheet was already open for that week. Nothing changed.'
        : `Opened ${result.total_slots} court hours across the next seven days.`,
    );
    sheet.reload();
  });

  const courts = sheet.data?.courts ?? [];
  const slots = sheet.data?.slots ?? [];

  // Hours down the side. Courts can differ in what time they stop — an
  // unlit court is done at six — so the rows are the union, and a court
  // with nothing at eight simply has no cell there.
  const hours = [...new Set(slots.map((s) => s.starts_at))].sort();
  const byKey = new Map(slots.map((s) => [`${s.starts_at}|${s.court_id}`, s]));

  const cut = firstVisible(hours.map((h) => ({ startsAt: h })), date, zone, wholeDay);
  const from = cut < 0 ? 0 : cut;
  const visibleHours = from > 0 ? hours.slice(from) : hours;

  const booked = slots.reduce((a, s) => a + s.players_booked, 0);
  const capacity = slots
    .filter((s) => s.slot_status === 'open')
    .reduce((a, s) => a + s.max_players, 0);
  const blocked = slots.filter((s) => s.slot_status === 'blocked').length;
  const uncharged = slots.flatMap((s) =>
    s.reservations.flatMap((r) =>
      r.players.filter((p) => p.checked_in_at && !p.charged && p.player_kind !== 'professional'),
    ),
  );

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Court Sheet</h1>
          <div className="muted small">
            <strong>{dayLabel(date, zone)}</strong> · {sheet.data?.day_type ?? '—'} ·{' '}
            {courts.length} court(s)
          </div>
        </div>
        <div className="spacer" />

        <select
          value={sport}
          onChange={(e) => setSport(e.target.value as typeof sport)}
          style={{ maxWidth: 180 }}
        >
          <option value="all">Every court</option>
          <option value="tennis">Tennis only</option>
          <option value="pickleball">Pickleball only</option>
        </select>

        <button className="secondary small" onClick={() => setDate(shiftDate(date, -1))}>
          ‹
        </button>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{ maxWidth: 170 }}
        />
        <button className="secondary small" onClick={() => setDate(shiftDate(date, 1))}>
          ›
        </button>

        {date === clubToday(zone) && (
          <label className="sheet-toggle">
            <input
              type="checkbox"
              checked={wholeDay}
              onChange={(e) => setWholeDay(e.target.checked)}
            />
            <span>From the start of the day</span>
          </label>
        )}

        {canOpenSheet && (
          <button onClick={() => openWeek.run()} disabled={openWeek.busy}>
            {openWeek.busy ? 'Opening…' : 'Open the week'}
          </button>
        )}
      </div>

      <ErrorBanner error={sheet.error} />
      <ErrorBanner error={openWeek.error} />

      {/* Court priority, guest play, whatever the club wrote down. */}
      <BylawNotices surface="racquet.booking" />

      {note && (
        <Card>
          <Status tone="good">{note}</Status>
        </Card>
      )}

      {slots.length > 0 && (
        <div className="tiles">
          <Tile label="Places Taken" value={`${booked} of ${capacity}`} />
          <Tile
            label="Courts Out of Play"
            value={blocked}
            note={blocked > 0 ? 'blocked or held' : undefined}
            tone={blocked > 0 ? 'warning' : undefined}
          />
          <Tile
            label="Checked In, Not Charged"
            value={uncharged.length}
            note={uncharged.length > 0 ? 'court time nobody has billed' : undefined}
            tone={uncharged.length > 0 ? 'warning' : undefined}
          />
        </div>
      )}

      {open && (
        <ReservationCard
          reservation={open.reservation}
          slot={open.slot}
          canRecord={isStaff}
          onDone={(message) => {
            setOpen(null);
            setNote(message ?? null);
            sheet.reload();
          }}
          onClose={() => setOpen(null)}
        />
      )}

      <Card flush>
        {sheet.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={10} />
          </div>
        ) : slots.length === 0 ? (
          <Empty>
            The sheet is not open for {fmtDate(date)}.{' '}
            {canOpenSheet
              ? 'Open the week to write its court hours.'
              : 'A manager has to open it first.'}
          </Empty>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 72 }}>Time</th>
                  {courts.map((c) => (
                    <th key={c.court_id} style={{ minWidth: 150 }}>
                      {c.name}
                      <div className="muted small" style={{ fontWeight: 400 }}>
                        {courtNote(c)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {from > 0 && (
                  <tr className="sheet-earlier">
                    <td colSpan={courts.length + 1}>
                      <button className="linkish" onClick={() => setWholeDay(true)}>
                        Show the {from} earlier hour{from === 1 ? '' : 's'}
                      </button>
                    </td>
                  </tr>
                )}
                {visibleHours.map((hour) => (
                  <Fragment key={hour}>
                    <tr>
                      <td className="mono">{hhmm(hour)}</td>
                      {courts.map((c) => {
                        const slot = byKey.get(`${hour}|${c.court_id}`);
                        return (
                          <td key={c.court_id}>
                            {slot ? (
                              <SlotCell
                                slot={slot}
                                booking={booking?.court_slot_id === slot.court_slot_id}
                                onBook={() =>
                                  setBooking((o) =>
                                    o?.court_slot_id === slot.court_slot_id ? null : slot,
                                  )
                                }
                                onOpen={(r) => setOpen({ reservation: r, slot })}
                              />
                            ) : (
                              <span className="muted small">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {/* The form opens under the hour it is for. It used to
                        render above the grid, so booking the 4pm court
                        scrolled the sheet away and left somebody filling
                        in a form with no idea which court it was for. */}
                    {booking && booking.starts_at === hour && (
                      <tr className="sheet-form">
                        <td colSpan={courts.length + 1}>
                          <BookingForm
                            slot={booking}
                            onDone={(message) => {
                              setBooking(null);
                              setNote(message);
                              sheet.reload();
                            }}
                            onCancel={() => setBooking(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

/**
 * One cell.
 *
 * Four states, and the fourth is the one this module exists for: a court
 * with nothing on it that still cannot be sold, because the ground it is
 * painted on is taken. That is drawn differently from an empty court on
 * purpose — an empty-looking cell the desk cannot sell is exactly the
 * cell that produces an argument.
 */
function SlotCell({
  slot,
  booking,
  onBook,
  onOpen,
}: {
  slot: Slot;
  /** True while this cell's booking form is the one that is open. */
  booking: boolean;
  onBook: () => void;
  onOpen: (r: Reservation) => void;
}) {
  if (slot.reservations.length > 0) {
    return (
      <>
        {slot.reservations.map((r) => (
          <button
            key={r.reservation_id}
            className="secondary small"
            style={{ width: '100%', textAlign: 'left', marginBottom: 4 }}
            onClick={() => onOpen(r)}
          >
            <span className="muted small" style={{ display: 'block' }}>
              {sportLabel(slot.sport)} · {r.play_kind}
              {r.status === 'checked_in' ? ' · checked in' : ''}
            </span>
            {r.players.length > 0
              ? r.players.map((p) => p.name).join(', ')
              : 'nobody named yet'}
          </button>
        ))}
        {slot.spaces_left > 0 && slot.slot_status === 'open' && (
          <button
            className={booking ? 'small' : 'secondary small'}
            onClick={onBook}
            aria-expanded={booking}
          >
            {booking ? 'Close' : `+ ${slot.spaces_left}`}
          </button>
        )}
      </>
    );
  }

  if (slot.slot_status !== 'open') {
    return (
      <Status tone={slot.slot_status === 'blocked' ? 'critical' : 'warning'}>
        {slot.slot_status === 'lesson' ? 'lesson' : slot.slot_status}
        {slot.slot_note ? ` — ${slot.slot_note}` : ''}
      </Status>
    );
  }

  if (slot.taken_by_court_name) {
    return (
      <span className="muted small">
        {slot.taken_by_court_name} has this ground
        {slot.taken_by_member ? ` (${slot.taken_by_member})` : ''}
      </span>
    );
  }

  return (
    <button
      className={booking ? 'small' : 'secondary small'}
      onClick={onBook}
      aria-expanded={booking}
    >
      {booking ? 'Close' : 'Book'}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Booking                                                             */
/* ------------------------------------------------------------------ */

function BookingForm({
  slot,
  onDone,
  onCancel,
}: {
  slot: Slot;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const { atLeast } = useSession();
  const isStaff = atLeast('staff');

  const [account, setAccount] = useState<AccountHit | null>(null);
  const [playKind, setPlayKind] = useState<'singles' | 'doubles'>('doubles');
  const [players, setPlayers] = useState<
    { player_kind: string; display_name: string; sponsor_person_id?: string }[]
  >([]);
  const [guestName, setGuestName] = useState('');

  const book = useMutation(async () => {
    await api('/v1/tennis/reservations', {
      method: 'POST',
      body: {
        court_slot_id: slot.court_slot_id,
        play_kind: playKind,
        ...(isStaff && account ? { member_account_id: account.member_account_id } : {}),
        players,
      },
    });
    onDone(
      `${slot.court_name} (${sportLabel(slot.sport).toLowerCase()}) at ` +
        `${hhmm(slot.starts_at)} is booked${
          account ? ` to ${account.display_name}` : ''
        }.`,
    );
  });

  return (
    <Card
      title={
        `${slot.court_name} · ${sportLabel(slot.sport)} · ${hhmm(slot.starts_at)}` +
        ` · ${slot.slot_minutes} minutes`
      }
      actions={
        <div className="actions">
          <button className="secondary small" onClick={onCancel}>
            Close
          </button>
        </div>
      }
    >
      <ErrorBanner error={book.error} />

      <div className="grid-2">
        {/* Staff book for anybody, so they need to find the membership.
            A member books for themselves and the server fills that in
            from the token — asking them would be asking a question with
            one possible answer.

            The account, not the membership: it is the number printed on
            the member's card, and the search behind it now finds the
            household by the spouse or the child standing at the desk as
            well as by the name the account is filed under. The server
            turns it back into the membership, which is what the
            committee's rules key on. */}
        {isStaff && (
          <AccountLookup
            label="Whose court is it"
            value={account}
            onChange={setAccount}
          />
        )}

        <label className="field">
          <span>Singles or doubles</span>
          <select
            value={playKind}
            onChange={(e) => setPlayKind(e.target.value as 'singles' | 'doubles')}
          >
            <option value="doubles">Doubles</option>
            <option value="singles">Singles</option>
          </select>
        </label>
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <span>Guests</span>
        <div className="hint">
          A guest needs a member answerable for them — the club bills the guest fee to that
          member&apos;s account, and the server works out which account that is.
        </div>
        <div className="row" style={{ marginTop: 6 }}>
          <input
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Guest's name"
            style={{ maxWidth: 260 }}
          />
          <button
            className="secondary small"
            disabled={!guestName.trim()}
            onClick={() => {
              setPlayers([
                ...players,
                { player_kind: 'member_guest', display_name: guestName.trim() },
              ]);
              setGuestName('');
            }}
          >
            Add
          </button>
        </div>
        {players.length > 0 && (
          <ul className="small" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {players.map((p, i) => (
              <li key={i}>
                {p.display_name}{' '}
                <button
                  className="secondary small"
                  onClick={() => setPlayers(players.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="actions" style={{ marginTop: 12 }}>
        <button
          className="gold"
          disabled={book.busy || (isStaff && !account)}
          onClick={() => book.run()}
        >
          {book.busy ? 'Booking…' : 'Take the court'}
        </button>
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* One reservation                                                     */
/* ------------------------------------------------------------------ */

function ReservationCard({
  reservation,
  slot,
  canRecord,
  onDone,
  onClose,
}: {
  reservation: Reservation;
  slot: Slot;
  canRecord: boolean;
  onDone: (message?: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');

  const checkIn = useMutation(async () => {
    await api(`/v1/tennis/reservations/${reservation.reservation_id}/check-in`, {
      method: 'POST',
    });
    onDone('Checked in.');
  });

  const cancel = useMutation(async () => {
    const result = await api<{ canceled_late: boolean }>(
      `/v1/tennis/reservations/${reservation.reservation_id}/cancel`,
      { method: 'POST', body: { reason: reason.trim() || null } },
    );
    onDone(
      result.canceled_late
        ? 'The court is back on the sheet. It is recorded as a late cancellation.'
        : 'The court is back on the sheet.',
    );
  });

  return (
    <Card
      title={
        `${slot.court_name} · ${sportLabel(slot.sport)} · ${hhmm(slot.starts_at)}` +
        ` · ${reservation.play_kind} · ${reservation.status.replace('_', ' ')}`
      }
      actions={
        <div className="actions">
          <button className="secondary small" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      <ErrorBanner error={checkIn.error} />
      <ErrorBanner error={cancel.error} />

      {reservation.players.length === 0 ? (
        <Empty>Nobody has been named on this court yet.</Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Player</th>
              <th style={{ width: 160 }}>Playing as</th>
              <th style={{ width: 180 }}>Guest of</th>
            </tr>
          </thead>
          <tbody>
            {reservation.players.map((p) => (
              <tr key={p.player_id}>
                <td className="num">{p.position}</td>
                <td>{p.name}</td>
                <td className="muted small">{p.player_kind.replace('_', ' ')}</td>
                <td className="muted small">{p.sponsor_name ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        {canRecord && reservation.status === 'booked' && (
          <button disabled={checkIn.busy} onClick={() => checkIn.run()}>
            {checkIn.busy ? 'Working…' : 'They turned up'}
          </button>
        )}
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why it is coming back (optional)"
          style={{ maxWidth: 280 }}
        />
        <button className="danger" disabled={cancel.busy} onClick={() => cancel.run()}>
          {cancel.busy ? 'Working…' : 'Give the court back'}
        </button>
      </div>

      <div className="hint" style={{ marginTop: 8 }}>
        A cancellation inside the club&apos;s cutoff is recorded as late rather than refused.
        Refusing it would leave a court held by somebody who is not coming.
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* What a member is holding                                            */
/* ------------------------------------------------------------------ */

/**
 * The one screen in this module a member opens twice.
 *
 * It shows the membership's courts, not only the ones booked under this
 * person's own name — a spouse who cannot see the court the other one
 * booked telephones the desk to ask about it, which is the call this
 * whole module is meant to stop.
 */
export function MyCourtsPage() {
  const [includePast, setIncludePast] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const mine = useApi<{ reservations: MyReservation[]; holds_courts: boolean }>(
    `/v1/tennis/my-reservations${includePast ? '?include_past=true' : ''}`,
    [includePast],
  );

  const cancel = useMutation(async (id: string) => {
    const result = await api<{ canceled_late: boolean }>(
      `/v1/tennis/reservations/${id}/cancel`,
      { method: 'POST', body: {} },
    );
    setNote(
      result.canceled_late
        ? 'Given back, and recorded as a late cancellation.'
        : 'Given back.',
    );
    mine.reload();
  });

  const rows = mine.data?.reservations ?? [];

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>My Courts</h1>
          <div className="muted small">What this membership is holding</div>
        </div>
        <div className="spacer" />
        <button
          className={includePast ? 'gold small' : 'secondary small'}
          onClick={() => setIncludePast(!includePast)}
        >
          {includePast ? 'Showing everything' : 'Show past courts too'}
        </button>
      </div>

      <ErrorBanner error={mine.error} />
      <ErrorBanner error={cancel.error} />

      {note && (
        <Card>
          <Status tone="good">{note}</Status>
        </Card>
      )}

      <Card flush>
        {mine.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={4} />
          </div>
        ) : mine.data && !mine.data.holds_courts ? (
          <Empty>
            This login is a staff login rather than a member&apos;s, so it holds no courts of
            its own. The court sheet is where everybody else&apos;s are.
          </Empty>
        ) : rows.length === 0 ? (
          <Empty>Nothing booked. The court sheet is where the courts are.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Day</th>
                <th style={{ width: 90 }}>Time</th>
                <th style={{ width: 110 }}>Sport</th>
                <th>Court</th>
                <th style={{ width: 130 }}>Status</th>
                <th style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.reservation_id}>
                  <td>{fmtDate(r.play_date)}</td>
                  <td className="mono">{hhmm(r.starts_at)}</td>
                  <td>{sportLabel(r.sport)}</td>
                  <td>
                    {r.court_name}
                    <span className="muted small"> · {r.play_kind}</span>
                  </td>
                  <td>
                    {r.status === 'canceled' && r.canceled_late ? (
                      <Status tone="warning">canceled late</Status>
                    ) : r.status === 'no_show' ? (
                      <Status tone="serious">no show</Status>
                    ) : (
                      <span className="muted small">{r.status.replace('_', ' ')}</span>
                    )}
                  </td>
                  <td>
                    {(r.status === 'booked' || r.status === 'checked_in') && (
                      <button
                        className="secondary small"
                        disabled={cancel.busy}
                        onClick={() => cancel.run(r.reservation_id)}
                      >
                        Give it back
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
